import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { ProductPreviewWithDiscount } from "../../../domain/projections/ProductPreviewWithDiscount";
import { Experiments } from "../../../domain/repositories/ABTestClient";
import { experimentNameShortener } from "./experimentNameShortener";
import { List, PROJECT, TrackingEventName, ViewItemListTrackingEvent } from "./tracking";

interface TrackCategoryViewItemListFunctionArgs {
  readonly experiments: Experiments | undefined;
  readonly list: List.COMPLETE_YOUR_LOOK | List.TRENDING_FOR_YOU | List.CATEGORIES | List.SALES;
  readonly categoryId: string;
  readonly products: ProductPreviewWithDiscount[];
}

interface TrackCategoryViewItemListFunction {
  (args: TrackCategoryViewItemListFunctionArgs): void;
}

interface UseTrackCategoryViewItemListFunctionArgs {
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackCategoryViewItemListFunction {
  (agrs: UseTrackCategoryViewItemListFunctionArgs): TrackCategoryViewItemListFunction;
}

const useTrackCategoryViewItemList: UseTrackCategoryViewItemListFunction = ({
  country,
  segment,
  tradename,
  userId,
}) => {
  const emitUserEvent = useEmitUserEvent<ViewItemListTrackingEvent>();

  const trackCategoryViewItemList: TrackCategoryViewItemListFunction = useCallback(
    ({ experiments, list, categoryId, products }) => {
      emitUserEvent({
        event: TrackingEventName.VIEW_ITEM_LIST,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        experiments: experiments ? JSON.stringify(experimentNameShortener(experiments)) : undefined,
        ecommerce: {
          // @ts-ignore
          impressions: products.map((product) => ({
            quantity: 1,
            variant: "(not set)",
            index: 1,
            look_id: "(not set)",
            name: product.title,
            id: product.id,
            price: product.price.amount,
            brand: product.brand,
            category: categoryId,
            list,
          })),
        },
      });
    },
    [country, emitUserEvent, segment, tradename, userId],
  );

  return trackCategoryViewItemList;
};

export { useTrackCategoryViewItemList };
